import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { genres } from "@/mocks/mv";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/charts", label: "排行榜" },
  { to: "/search", label: "搜索" },
  { to: "/about", label: "关于" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setCatOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
    setMenuOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
      isActive
        ? "text-primary-400"
        : "text-foreground-300 hover:text-foreground-50"
    }`;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? "bg-background-950/95 backdrop-blur border-b border-background-800"
          : "bg-gradient-to-b from-background-950/90 to-transparent"
      }`}
    >
      <div className="flex h-16 w-full items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-lg text-background-50">
            <i className="ri-play-circle-fill" aria-hidden="true"></i>
          </span>
          <span className="font-heading text-lg font-bold tracking-wide text-foreground-50">
            红桃<span className="text-primary-400">MV</span>视频
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          <NavLink to="/" className={linkClass} end>
            首页
          </NavLink>
          <div
            className="relative"
            onMouseEnter={() => setCatOpen(true)}
            onMouseLeave={() => setCatOpen(false)}
          >
            <button
              type="button"
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium text-foreground-300 transition-colors hover:text-foreground-50"
            >
              分类
              <i className="ri-arrow-down-s-line text-base" aria-hidden="true"></i>
            </button>
            {catOpen && (
              <div className="absolute left-0 top-full grid w-[440px] grid-cols-2 gap-1 rounded-lg border border-background-800 bg-background-900 p-2">
                {genres.map((g) => (
                  <Link
                    key={g.code}
                    to={`/category/${g.code}`}
                    className="flex items-center gap-3 rounded-md px-3 py-2 transition-colors hover:bg-background-800"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500/15 text-primary-400">
                      <i className={`${g.icon} text-base`} aria-hidden="true"></i>
                    </span>
                    <span className="text-sm text-foreground-200">{g.name}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
          {navItems.slice(1).map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form
          onSubmit={submitSearch}
          className="ml-auto hidden items-center gap-2 rounded-full border border-background-700 bg-background-900 px-3 py-1.5 md:flex"
        >
          <i className="ri-search-line text-base text-foreground-400" aria-hidden="true"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索 MV 名称或歌手"
            name="nav_search"
            className="w-40 bg-transparent text-sm text-foreground-100 outline-none placeholder:text-foreground-500 lg:w-56"
          />
        </form>

        <Link
          to="/charts"
          className="ml-auto hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:ml-0 md:inline-flex"
        >
          热门榜单
        </Link>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-700 text-xl text-foreground-100 md:hidden"
        >
          <i className={menuOpen ? "ri-close-line" : "ri-menu-line"} aria-hidden="true"></i>
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-800 bg-background-950 px-4 py-4 md:hidden">
          <form onSubmit={submitSearch} className="mb-3 flex items-center gap-2 rounded-md border border-background-700 bg-background-900 px-3 py-2">
            <i className="ri-search-line text-base text-foreground-400" aria-hidden="true"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索 MV 名称或歌手"
              name="mobile_search"
              className="w-full bg-transparent text-sm text-foreground-100 outline-none placeholder:text-foreground-500"
            />
          </form>
          <div className="flex flex-col gap-1">
            <Link to="/" className="rounded-md px-3 py-2 text-sm text-foreground-200 hover:bg-background-800">首页</Link>
            <div className="px-3 pt-2 text-xs text-foreground-500">分类</div>
            <div className="grid grid-cols-2 gap-1">
              {genres.map((g) => (
                <Link key={g.code} to={`/category/${g.code}`} className="rounded-md px-3 py-2 text-sm text-foreground-300 hover:bg-background-800">
                  {g.name}
                </Link>
              ))}
            </div>
            <Link to="/charts" className="mt-1 rounded-md px-3 py-2 text-sm text-foreground-200 hover:bg-background-800">排行榜</Link>
            <Link to="/search" className="rounded-md px-3 py-2 text-sm text-foreground-200 hover:bg-background-800">搜索</Link>
            <Link to="/about" className="rounded-md px-3 py-2 text-sm text-foreground-200 hover:bg-background-800">关于我们</Link>
          </div>
        </div>
      )}
    </header>
  );
}